import React from 'react';
import {connect} from 'react-redux';

import {pluralize} from 'BenchlingUtil';
import {BenchlingDispatch} from 'redux-stores/Store';
import {bulkUpdateRequests, UpdateRequestArgs} from 'requests/inputs/InputReduxActions';
import {Request} from 'requests/RequestConstants';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {OptionContentType} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

import BulkUpdateRequestsModal, {FieldToUpdate} from './BulkUpdateRequestsModal';

export interface BulkUpdateRequestsButtonProps {
  buttonStyle: ButtonStyle;
  className?: string;
  requests: Array<Request>;
}

interface DispatchProps {
  onBulkUpdateRequests: (requestIds: Array<string>, data: UpdateRequestArgs) => Promise<void>;
}

function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
  return {
    onBulkUpdateRequests: (requestIds: Array<string>, data: UpdateRequestArgs) =>
      dispatch(bulkUpdateRequests(requestIds, data)),
  };
}

/** Button to update multiple requests. */
export function BulkUpdateRequestsButton(props: BulkUpdateRequestsButtonProps & DispatchProps): JSX.Element {
  function _areRequestSchemasIdentical(): boolean {
    const schemaId = props.requests[0].schemaId;
    for (const request of props.requests) {
      if (request.schemaId !== schemaId) {
        return false;
      }
    }
    return true;
  }

  function _getTooltipTitle(): string {
    if (props.requests.length === 0) {
      return 'Select request(s) to edit';
    }

    if (_areRequestSchemasIdentical()) {
      return `Edit ${pluralize(props.requests.length, 'request')}...`;
    }
    return 'Cannot bulk edit requests of different schemas';
  }

  const handleBulkUpdateRequests = async (fieldToUpdate: FieldToUpdate): Promise<void> => {
    await legacyRunModal(BulkUpdateRequestsModal, {
      schemaId: props.requests.length > 0 ? props.requests[0].schemaId : '',
      requests: props.requests,
      fieldToUpdate,
      onRequestSubmit: (data: UpdateRequestArgs) =>
        props.onBulkUpdateRequests(
          props.requests.map(({id}) => id),
          data
        ),
    });
  };

  return (
    <div className="buttonGroup">
      <Tooltip title={_getTooltipTitle()} placement={TooltipPlacement.TOP}>
        <Dropdown
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.SMALL}>
              <Option
                contentType={OptionContentType.PLAIN}
                onSelect={async () => {
                  await handleBulkUpdateRequests(FieldToUpdate.ASSIGNEES);
                }}
              >
                Assignees
              </Option>
              <Option
                contentType={OptionContentType.PLAIN}
                onSelect={async () => {
                  await handleBulkUpdateRequests(FieldToUpdate.SCHEDULED_ON);
                }}
              >
                Scheduled On
              </Option>
            </Menu>
          }
        >
          <Button
            buttonStyle={props.buttonStyle}
            className={props.className}
            disabled={props.requests.length === 0 || !_areRequestSchemasIdentical()}
            icon={<BenchlingIcon icon="edit" />}
            isDropdown={true}
            size={ButtonSize.SMALL}
          />
        </Dropdown>
      </Tooltip>
    </div>
  );
}

export default connect(null, mapDispatchToProps)(BulkUpdateRequestsButton);
